/**
 * Build room-level creative grounding from a compiled survey JSON for
 * experiment-report twin chat (creatives text + stimulus image refs).
 */

import type {
  StimulusImage,
  StudyCreative,
} from '@digital-twins/shared-types'

interface StudyCreativeGrounding {
  creatives: Array<StudyCreative>
  stimulusImages: Array<StimulusImage>
}

function labelFromStableKey(stableKey: string): string {
  return stableKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  return value as Record<string, unknown>
}

function readItems(container: unknown): Array<Record<string, unknown>> {
  const obj = asRecord(container)
  const items = obj?.items
  if (!Array.isArray(items)) {
    return []
  }
  return items
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item != null)
}

export function buildStudyCreativeGrounding(
  survey: unknown,
): StudyCreativeGrounding {
  const root = asRecord(survey)
  const canonical = asRecord(root?._canonicalConfig)
  if (!canonical) {
    return { creatives: [], stimulusImages: [] }
  }

  const assetItems = readItems(canonical.assetContext)
  const imageItems = readItems(canonical.assetImageContext)
  const summaryByAssetId = new Map<string, string>()
  for (const item of readItems(canonical.visualSummaries)) {
    const assetId = item.assetId
    const summary = item.summary
    if (typeof assetId === 'string' && typeof summary === 'string' && summary.trim()) {
      summaryByAssetId.set(assetId, summary.trim())
    }
  }
  const summariesMap = asRecord(canonical.visualSummaries)
  if (summariesMap && !Array.isArray(summariesMap.items)) {
    for (const [assetId, summary] of Object.entries(summariesMap)) {
      if (assetId === 'schemaVersion' || assetId === 'items') {
        continue
      }
      if (typeof summary === 'string' && summary.trim()) {
        summaryByAssetId.set(assetId, summary.trim())
      }
    }
  }

  const imageByAssetId = new Map<string, Record<string, unknown>>()
  for (const img of imageItems) {
    if (typeof img.assetId === 'string') {
      imageByAssetId.set(img.assetId, img)
    }
  }

  const creatives: Array<StudyCreative> = []
  const stimulusImages: Array<StimulusImage> = []

  for (const asset of assetItems) {
    if (typeof asset.assetId !== 'string' || !asset.assetId) {
      continue
    }
    const stableKey =
      typeof asset.assetStableKey === 'string' && asset.assetStableKey
        ? asset.assetStableKey
        : asset.assetId
    const label = labelFromStableKey(stableKey)
    const title =
      typeof asset.title === 'string' && asset.title.trim()
        ? asset.title.trim()
        : undefined
    const description =
      typeof asset.description === 'string' && asset.description.trim()
        ? asset.description.trim()
        : undefined
    const visualSummary = summaryByAssetId.get(asset.assetId)

    creatives.push({
      assetId: asset.assetId,
      label,
      ...(title ? { title } : {}),
      ...(description ? { description } : {}),
      ...(visualSummary ? { visualSummary } : {}),
    })

    const image = imageByAssetId.get(asset.assetId)
    const objectKey =
      typeof image?.objectKey === 'string' && String(image.objectKey).trim()
        ? String(image.objectKey).trim()
        : undefined
    if (objectKey) {
      stimulusImages.push({
        label,
        imagesObjectKey: objectKey,
      })
    }
  }

  return { creatives, stimulusImages }
}
